import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router';
import { PageHead } from '@/admin-area/ui/PageHead';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioOption } from '@/components/ui/radio-group';
import { EXPENSES_MANAGE, holds, useExpensesPermissions } from '../use-expenses-permissions';
import { getMileageSetting, putMileageSetting, type MileageSetting } from './api';
import { formatRate, parseRate } from './calc';

type Mode = 'aci' | 'fixed' | 'off';
const MODES: readonly Mode[] = ['aci', 'fixed', 'off'];

/**
 * Gestione's mileage settings: how the €/km is set (ACI table, a fixed rate, or off), the fixed
 * rate, and the yearly distance of the ACI table in use. Audited by the server; it applies to the
 * trips entered afterwards.
 */
export function MileageSettingsPage() {
  const permissions = useExpensesPermissions();
  if (permissions.status === 'loading') return null;
  if (!holds(permissions, EXPENSES_MANAGE)) return <Navigate to="/expenses" replace />;
  return <SettingsForm />;
}

function SettingsForm() {
  const { t } = useTranslation('expenses');
  const [loaded, setLoaded] = useState<'loading' | 'failed' | 'ready'>('loading');
  const [mode, setMode] = useState<Mode | ''>('');
  const [rate, setRate] = useState('');
  const [refKm, setRefKm] = useState('');
  const [saved, setSaved] = useState<'idle' | 'saved' | 'failed' | 'rateRequired'>('idle');

  useEffect(() => {
    let live = true;
    void getMileageSetting().then((outcome) => {
      if (!live) return;
      if (!outcome.ok) return setLoaded('failed');
      fill(outcome.value);
      setLoaded('ready');
    });
    return () => {
      live = false;
    };
  }, []);

  const fill = (setting: MileageSetting): void => {
    setMode(setting.mode ?? '');
    setRate(setting.fixedRate ? formatRate(setting.fixedRate) : '');
    setRefKm(setting.aciReferenceKm === null ? '' : String(setting.aciReferenceKm));
  };

  const rateOk = parseRate(rate) !== null;
  const refOk = refKm.trim() === '' || /^\d{1,6}$/.test(refKm.trim());
  const valid = mode !== '' && (mode !== 'fixed' || rateOk) && refOk;

  const save = async (): Promise<void> => {
    if (mode === '' || !valid) return;
    const outcome = await putMileageSetting({
      mode,
      ...(mode === 'fixed' ? { fixedRate: parseRate(rate) } : {}),
      ...(mode === 'aci' && refKm.trim() !== '' ? { aciReferenceKm: Number(refKm) } : {}),
    });
    if (outcome.ok) {
      fill(outcome.value);
      setSaved('saved');
    } else setSaved(outcome.code === 'RATE_REQUIRED' ? 'rateRequired' : 'failed');
  };

  return (
    <section aria-label={t('mileage.settings.title')} className="flex max-w-xl flex-col gap-4">
      <PageHead title={t('mileage.settings.title')} summary={t('mileage.settings.summary')} />
      {loaded === 'loading' ? <p role="status">{t('mileage.loading')}</p> : null}
      {loaded === 'failed' ? <p role="alert">{t('mileage.loadFailed')}</p> : null}
      {loaded === 'ready' ? (
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            setSaved('idle');
            void save();
          }}
        >
          <ModeFields {...{ mode, setMode, rate, setRate, refKm, setRefKm, rateOk, refOk }} />
          {saved === 'saved' ? <p role="status">{t('mileage.settings.saved')}</p> : null}
          {saved === 'rateRequired' || saved === 'failed' ? (
            <p role="alert" className="text-destructive text-sm">
              {t(`mileage.settings.${saved}`)}
            </p>
          ) : null}
          <div>
            <Button type="submit" disabled={!valid}>
              {t('mileage.settings.save')}
            </Button>
          </div>
        </form>
      ) : null}
    </section>
  );
}

type ModeProps = {
  readonly mode: Mode | '';
  readonly setMode: (mode: Mode) => void;
  readonly rate: string;
  readonly setRate: (rate: string) => void;
  readonly refKm: string;
  readonly setRefKm: (km: string) => void;
  readonly rateOk: boolean;
  readonly refOk: boolean;
};

/** The mode choice, and the one input the chosen mode needs. */
function ModeFields(props: ModeProps) {
  const { mode, setMode, rate, setRate, refKm, setRefKm, rateOk, refOk } = props;
  const { t } = useTranslation('expenses');
  return (
    <>
      <RadioGroup legend={t('mileage.settings.mode')}>
        {MODES.map((value) => (
          <RadioOption
            key={value}
            name="mode"
            label={t(`mileage.settings.modes.${value}`)}
            help={t(`mileage.settings.modeHelp.${value}`)}
            checked={mode === value}
            onSelect={() => setMode(value)}
          />
        ))}
      </RadioGroup>
      {mode === 'fixed' ? (
        <label className="flex flex-col gap-1 text-sm font-semibold">
          {t('mileage.settings.fixedRate')}
          <Input
            inputMode="decimal"
            value={rate}
            aria-invalid={!rateOk}
            onChange={(event) => setRate(event.target.value)}
          />
        </label>
      ) : null}
      {mode === 'aci' ? (
        <label className="flex flex-col gap-1 text-sm font-semibold">
          {t('mileage.settings.referenceKm')}
          <Input
            inputMode="numeric"
            value={refKm}
            aria-invalid={!refOk}
            onChange={(event) => setRefKm(event.target.value)}
          />
          <small className="text-muted-foreground text-sm font-normal">
            {t('mileage.settings.referenceKmHelp')}
          </small>
        </label>
      ) : null}
    </>
  );
}
